import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiSave, FiArrowLeft, FiLoader, FiTarget, FiList, FiPlus, FiTrash2, FiEdit2 } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { funisProspeccaoAPI, etapasAPI } from '../services/api';
import theme from '../styles/theme';
import Layout from '../components/Layout';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[4]};
`;

const BackButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: transparent;
  border: 1px solid ${theme.colors.border.primary};
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.primary[500]};
    border-color: ${theme.colors.primary[500]};
  }
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  margin: 0;
`;

const Form = styled.form`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[8]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const SectionTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${theme.spacing[4]};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
`;

const Required = styled.span`
  color: ${theme.colors.error[500]};
`;

const Input = styled.input`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Textarea = styled.textarea`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  transition: all 0.2s ease;
  resize: vertical;
  min-height: 100px;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.base};
  cursor: pointer;
`;

const EtapasList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const EtapaItem = styled.div`
  background: ${theme.colors.background.primary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
`;

const EtapaInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
`;

const EtapaName = styled.div`
  font-weight: 600;
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
`;

const EtapaDetails = styled.div`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
  display: flex;
  gap: ${theme.spacing[3]};
`;

const EtapaActions = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
`;

const IconButton = styled.button`
  padding: ${theme.spacing[2]};
  border: 1px solid ${props => props.$danger ? theme.colors.error[500] : theme.colors.border.primary};
  background: ${props => props.$danger ? theme.colors.error[500] : 'transparent'};
  color: ${props => props.$danger ? theme.colors.text.primary : theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${props => props.$danger ? theme.colors.error[600] : theme.colors.background.secondary};
    border-color: ${props => props.$danger ? theme.colors.error[600] : theme.colors.primary[500]};
    color: ${props => props.$danger ? theme.colors.text.primary : theme.colors.primary[500]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AddEtapaButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 2px dashed ${theme.colors.border.primary};
  background: transparent;
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};

  &:hover {
    border-color: ${theme.colors.primary[500]};
    color: ${theme.colors.primary[500]};
    background: ${theme.colors.primary[500]}10;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing[4]};
  justify-content: flex-end;
  padding-top: ${theme.spacing[6]};
  border-top: 1px solid ${theme.colors.border.primary};
`;

const Button = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border: 1px solid ${props => {
    if (props.$danger) return theme.colors.error[500];
    if (props.$primary) return theme.colors.primary[500];
    return theme.colors.border.primary;
  }};
  background: ${props => {
    if (props.$danger) return theme.colors.error[500];
    if (props.$primary) return theme.colors.primary[500];
    return 'transparent';
  }};
  color: ${props => {
    if (props.$danger || props.$primary) return theme.colors.text.primary;
    return theme.colors.text.secondary;
  }};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  min-width: 120px;
  justify-content: center;

  &:hover:not(:disabled) {
    background: ${props => {
      if (props.$danger) return theme.colors.error[600];
      if (props.$primary) return theme.colors.primary[600];
      return theme.colors.background.primary;
    }};
    color: ${props => {
      if (props.$danger || props.$primary) return theme.colors.text.primary;
      return theme.colors.primary[500];
    }};
    border-color: ${props => {
      if (props.$danger) return theme.colors.error[600];
      if (props.$primary) return theme.colors.primary[600];
      return theme.colors.primary[500];
    }};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background: ${theme.colors.error[500]}20;
  border: 1px solid ${theme.colors.error[500]};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  color: ${theme.colors.error[600]};
  font-size: ${theme.fontSizes.sm};
`;

const SuccessMessage = styled.div`
  background: ${theme.colors.success[500]}20;
  border: 1px solid ${theme.colors.success[500]};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  color: ${theme.colors.success[600]};
  font-size: ${theme.fontSizes.sm};
`;

// Modal para adicionar/editar etapa
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[8]};
  max-width: 500px;
  width: 90%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const ModalTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.xl};
  font-weight: 700;
  margin: 0;
`;

const ModalMessage = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.base};
  line-height: 1.5;
  margin: 0;
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  justify-content: flex-end;
`;

const ModalButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border: 1px solid ${props => props.$danger ? theme.colors.error[500] : theme.colors.border.primary};
  background: ${props => props.$danger ? theme.colors.error[500] : 'transparent'};
  color: ${props => props.$danger ? theme.colors.text.primary : theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover:not(:disabled) {
    background: ${props => props.$danger ? theme.colors.error[600] : theme.colors.background.primary};
    border-color: ${props => props.$danger ? theme.colors.error[600] : theme.colors.primary[500]};
    color: ${props => props.$danger ? theme.colors.text.primary : theme.colors.primary[500]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FunilProspeccaoForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  // Validar se o id é um UUID válido
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const isValidUUID = id && uuidRegex.test(id);
  const isEditing = isValidUUID;

  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    ativo: true
  });

  const [etapas, setEtapas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEditing);
  const [loadingEtapa, setLoadingEtapa] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showEtapaModal, setShowEtapaModal] = useState(false);
  const [etapaEditando, setEtapaEditando] = useState(null);
  const [etapaForm, setEtapaForm] = useState({ nome: '', ordem: '', descricao: '' });
  const [deletandoEtapa, setDeletandoEtapa] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [etapaParaExcluir, setEtapaParaExcluir] = useState(null);
  const [showDeleteFunilModal, setShowDeleteFunilModal] = useState(false);
  const [deletandoFunil, setDeletandoFunil] = useState(false);

  // Redirecionar se a URL tem um ID inválido (ex: /leads/funis/undefined/editar)
  useEffect(() => {
    // Se tem algum id na URL mas não é um UUID válido, redirecionar para lista
    if (id && !isValidUUID && id !== 'novo') {
      console.error('[FunilProspeccaoForm] ID inválido na URL, redirecionando:', id);
      navigate('/leads/funis');
    }
  }, [id, isValidUUID, navigate]);

  // Carregar dados do funil se estiver editando
  useEffect(() => {
    if (isEditing && isValidUUID) {
      carregarFunil();
    }
  }, [id, isEditing, isValidUUID]);

  const carregarFunil = async () => {
    try {
      setLoadingData(true);
      const response = await funisProspeccaoAPI.buscarPorId(id);
      
      if (response.data.success) {
        const funil = response.data.data;
        setFormData({
          nome: funil.nome || '',
          descricao: funil.descricao || '',
          ativo: funil.ativo
        });
        setEtapas(funil.etapas || []);
      } else {
        setError('Erro ao carregar dados do funil');
      }
    } catch (err) {
      console.error('Erro ao carregar funil:', err);
      setError('Erro ao conectar com o servidor');
    } finally {
      setLoadingData(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Validações básicas
      if (!formData.nome.trim()) {
        throw new Error('Nome é obrigatório');
      }

      let response;
      if (isEditing) {
        response = await funisProspeccaoAPI.atualizar(id, formData);
      } else {
        response = await funisProspeccaoAPI.criar(formData);
      }

      if (response.data.success) {
        setSuccess(isEditing ? 'Funil atualizado com sucesso!' : 'Funil criado com sucesso!');
        
        // Se foi criação, redirecionar para edição
        if (!isEditing && response.data.data.id) {
          setTimeout(() => {
            navigate(`/leads/funis/${response.data.data.id}/editar`);
          }, 1000);
        } else {
          setTimeout(() => {
            navigate('/leads/funis');
          }, 1500);
        }
      } else {
        setError(response.data.error || 'Erro ao salvar funil');
      }
    } catch (err) {
      console.error('Erro ao salvar funil:', err);
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Erro ao conectar com o servidor. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVoltar = () => {
    navigate('/leads/funis');
  };

  const abrirModalEtapa = (e, etapa = null) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (etapa) {
      setEtapaEditando(etapa);
      setEtapaForm({
        nome: etapa.nome || '',
        ordem: etapa.ordem?.toString() || '',
        descricao: etapa.descricao || ''
      });
    } else {
      setEtapaEditando(null);
      setEtapaForm({
        nome: '',
        ordem: etapas.length > 0 ? (Math.max(...etapas.map(e => e.ordem || 0)) + 1).toString() : '1',
        descricao: ''
      });
    }
    setShowEtapaModal(true);
  };

  const fecharModalEtapa = () => {
    setShowEtapaModal(false);
    setEtapaEditando(null);
    setEtapaForm({ nome: '', ordem: '', descricao: '' });
  };

  const salvarEtapa = async () => {
    try {
      setLoadingEtapa(true);
      setError(null);

      if (!etapaForm.nome.trim()) {
        setError('Nome da etapa é obrigatório');
        setLoadingEtapa(false);
        return;
      }

      if (!etapaForm.ordem || isNaN(parseInt(etapaForm.ordem))) {
        setError('Ordem da etapa é obrigatória e deve ser um número');
        setLoadingEtapa(false);
        return;
      }

      // Validar se temos o ID do funil antes de criar uma etapa
      if (!etapaEditando && !isValidUUID) {
        setError('ID do funil é inválido. Salve o funil primeiro antes de adicionar etapas.');
        setLoadingEtapa(false);
        return;
      }

      let dadosEtapa;
      let response;
      
      if (etapaEditando) {
        // Ao atualizar, não enviar funil_id (é obtido da etapa existente)
        dadosEtapa = {
          nome: etapaForm.nome.trim(),
          ordem: parseInt(etapaForm.ordem),
          descricao: etapaForm.descricao?.trim() || null
        };
        response = await etapasAPI.atualizar(etapaEditando.id, dadosEtapa);
      } else {
        // Ao criar, enviar funil_id
        dadosEtapa = {
          funil_id: id,
          nome: etapaForm.nome.trim(),
          ordem: parseInt(etapaForm.ordem),
          descricao: etapaForm.descricao?.trim() || null
        };
        response = await etapasAPI.criar(dadosEtapa);
      }

      if (response.data.success) {
        // Atualizar lista de etapas localmente (mais rápido que recarregar tudo)
        if (etapaEditando) {
          // Atualizar etapa existente e reordenar
          setEtapas(etapas.map(e => e.id === etapaEditando.id ? response.data.data : e).sort((a, b) => a.ordem - b.ordem));
        } else {
          // Adicionar nova etapa e reordenar
          setEtapas([...etapas, response.data.data].sort((a, b) => a.ordem - b.ordem));
        }
        fecharModalEtapa();
        setSuccess('Etapa salva com sucesso!');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(response.data.error || 'Erro ao salvar etapa');
      }
    } catch (err) {
      console.error('Erro ao salvar etapa:', err);
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Erro ao conectar com o servidor');
      }
    } finally {
      setLoadingEtapa(false);
    }
  };

  // Função para abrir modal de confirmação de exclusão
  const handleAbrirModalExclusao = (etapa) => {
    setEtapaParaExcluir(etapa);
    setShowDeleteModal(true);
  };

  // Função para cancelar exclusão
  const handleCancelarExclusao = () => {
    setShowDeleteModal(false);
    setEtapaParaExcluir(null);
  };

  // Função para confirmar e executar exclusão
  const handleConfirmarExclusao = async () => {
    if (!etapaParaExcluir) return;

    try {
      setDeletandoEtapa(etapaParaExcluir.id);
      setError(null);

      const response = await etapasAPI.deletar(etapaParaExcluir.id);

      if (response.data.success) {
        // Remover etapa da lista localmente (mais rápido que recarregar tudo)
        setEtapas(etapas.filter(e => e.id !== etapaParaExcluir.id));
        setSuccess('Etapa excluída com sucesso!');
        setTimeout(() => setSuccess(null), 3000);
        setShowDeleteModal(false);
        setEtapaParaExcluir(null);
      } else {
        setError(response.data.error || 'Erro ao excluir etapa');
      }
    } catch (err) {
      console.error('Erro ao excluir etapa:', err);
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Erro ao conectar com o servidor');
      }
    } finally {
      setDeletandoEtapa(null);
    }
  };

  // Função para abrir modal de exclusão do funil
  const handleAbrirModalExclusaoFunil = () => {
    setShowDeleteFunilModal(true);
  };

  // Função para cancelar exclusão do funil
  const handleCancelarExclusaoFunil = () => {
    setShowDeleteFunilModal(false);
  };

  // Função para confirmar e executar exclusão do funil
  const handleConfirmarExclusaoFunil = async () => {
    try {
      setDeletandoFunil(true);
      setError(null);

      const response = await funisProspeccaoAPI.deletar(id);

      if (response.data.success) {
        // Redirecionar para lista de funis
        navigate('/leads/funis');
      } else {
        setError(response.data.error || 'Erro ao excluir funil');
        setShowDeleteFunilModal(false);
      }
    } catch (err) {
      console.error('Erro ao excluir funil:', err);
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Erro ao conectar com o servidor');
      }
      setShowDeleteFunilModal(false);
    } finally {
      setDeletandoFunil(false);
    }
  };

  if (loadingData) {
    return (
      <Layout title="Carregando...">
        <Container>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
            <FiLoader style={{ fontSize: '2rem', animation: 'spin 1s linear infinite' }} />
          </div>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={isEditing ? 'Editar Funil' : 'Novo Funil'}>
      <Container>
        <Header>
          <BackButton onClick={handleVoltar}>
            <FiArrowLeft />
            Voltar
          </BackButton>
          <Title>
            {isEditing ? 'Editar Funil de Prospecção' : 'Novo Funil de Prospecção'}
          </Title>
        </Header>

        <Form onSubmit={handleSubmit}>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}

          <FormSection>
            <SectionTitle>
              <FiTarget />
              Informações do Funil
            </SectionTitle>
            
            <FormGrid>
              <FormGroup>
                <Label htmlFor="nome">
                  Nome <Required>*</Required>
                </Label>
                <Input
                  id="nome"
                  type="text"
                  placeholder="Nome do funil"
                  value={formData.nome}
                  onChange={(e) => handleInputChange('nome', e.target.value)}
                  required
                />
              </FormGroup>

              <FormGroup>
                <CheckboxGroup>
                  <Checkbox
                    id="ativo"
                    type="checkbox"
                    checked={formData.ativo}
                    onChange={(e) => handleInputChange('ativo', e.target.checked)}
                  />
                  <CheckboxLabel htmlFor="ativo">
                    Funil ativo
                  </CheckboxLabel>
                </CheckboxGroup>
              </FormGroup>
            </FormGrid>

            <FormGroup>
              <Label htmlFor="descricao">Descrição</Label>
              <Textarea
                id="descricao"
                placeholder="Descrição do funil..."
                value={formData.descricao}
                onChange={(e) => handleInputChange('descricao', e.target.value)}
              />
            </FormGroup>
          </FormSection>

          {isEditing && (
            <FormSection>
              <SectionTitle>
                <FiList />
                Etapas do Funil
              </SectionTitle>

              <EtapasList>
                {etapas.map((etapa) => (
                  <EtapaItem key={etapa.id}>
                    <EtapaInfo>
                      <EtapaName>{etapa.nome}</EtapaName>
                      <EtapaDetails>
                        <span>Ordem: {etapa.ordem}</span>
                        {etapa.descricao && <span>• {etapa.descricao}</span>}
                      </EtapaDetails>
                    </EtapaInfo>
                    <EtapaActions>
                      <IconButton
                        type="button"
                        onClick={(e) => abrirModalEtapa(e, etapa)}
                        title="Editar etapa"
                      >
                        <FiEdit2 />
                      </IconButton>
                      <IconButton
                        type="button"
                        $danger
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleAbrirModalExclusao(etapa);
                        }}
                        disabled={deletandoEtapa === etapa.id}
                        title="Excluir etapa"
                      >
                        {deletandoEtapa === etapa.id ? <FiLoader /> : <FiTrash2 />}
                      </IconButton>
                    </EtapaActions>
                  </EtapaItem>
                ))}
                {etapas.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '2rem', color: theme.colors.text.muted }}>
                    Nenhuma etapa cadastrada. Adicione uma etapa para começar.
                  </div>
                )}
              </EtapasList>

              <AddEtapaButton type="button" onClick={(e) => abrirModalEtapa(e)}>
                <FiPlus />
                Adicionar Etapa
              </AddEtapaButton>
            </FormSection>
          )}

          <Actions>
            <Button type="button" onClick={handleVoltar}>
              Cancelar
            </Button>
            {isEditing && (
              <Button type="button" onClick={handleAbrirModalExclusaoFunil} $danger>
                <FiTrash2 />
                Excluir Funil
              </Button>
            )}
            <Button $primary type="submit" disabled={loading}>
              {loading ? <FiLoader /> : <FiSave />}
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
          </Actions>
        </Form>
      </Container>

      {/* Modal de etapa */}
      {showEtapaModal && (
        <ModalOverlay onClick={fecharModalEtapa}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>
              {etapaEditando ? 'Editar Etapa' : 'Nova Etapa'}
            </ModalTitle>

            <FormGroup>
              <Label>
                Nome <Required>*</Required>
              </Label>
              <Input
                type="text"
                placeholder="Nome da etapa"
                value={etapaForm.nome}
                onChange={(e) => setEtapaForm({ ...etapaForm, nome: e.target.value })}
              />
            </FormGroup>

            <FormGroup>
              <Label>
                Ordem <Required>*</Required>
              </Label>
              <Input
                type="number"
                placeholder="Ordem da etapa"
                value={etapaForm.ordem}
                onChange={(e) => setEtapaForm({ ...etapaForm, ordem: e.target.value })}
                min="1"
              />
            </FormGroup>

            <FormGroup>
              <Label>Descrição</Label>
              <Textarea
                placeholder="Descrição da etapa..."
                value={etapaForm.descricao}
                onChange={(e) => setEtapaForm({ ...etapaForm, descricao: e.target.value })}
              />
            </FormGroup>

            <ModalActions>
              <Button type="button" onClick={fecharModalEtapa}>
                Cancelar
              </Button>
              <Button $primary type="button" onClick={salvarEtapa} disabled={loadingEtapa}>
                {loadingEtapa ? <FiLoader /> : <FiSave />}
                {loadingEtapa ? 'Salvando...' : 'Salvar'}
              </Button>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Modal de confirmação de exclusão de etapa */}
      {showDeleteModal && etapaParaExcluir && (
        <ModalOverlay onClick={handleCancelarExclusao}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle style={{ color: theme.colors.error[500] }}>Excluir Etapa</ModalTitle>
            <ModalMessage>
              Tem certeza que deseja excluir esta etapa? Esta ação não pode ser desfeita e irá excluir:
              <br /><br />
              • A etapa <strong>{etapaParaExcluir.nome}</strong> (Ordem: {etapaParaExcluir.ordem})
              {etapaParaExcluir.descricao && (
                <>
                  <br />
                  • Descrição: {etapaParaExcluir.descricao}
                </>
              )}
              <br />
              • Todas as mensagens associadas a esta etapa
              <br />
              • Todas as interações relacionadas
              <br />
              • O controle de prospecção associado
              <br /><br />
              <strong>Atenção:</strong> Se existirem leads nesta etapa, a exclusão não será permitida.
            </ModalMessage>
            <ModalActions>
              <ModalButton onClick={handleCancelarExclusao}>
                Cancelar
              </ModalButton>
              <ModalButton $danger onClick={handleConfirmarExclusao} disabled={deletandoEtapa === etapaParaExcluir.id}>
                <FiTrash2 />
                {deletandoEtapa === etapaParaExcluir.id ? 'Excluindo...' : 'Excluir'}
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Modal de confirmação de exclusão do funil */}
      {showDeleteFunilModal && (
        <ModalOverlay onClick={handleCancelarExclusaoFunil}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle style={{ color: theme.colors.error[500] }}>Excluir Funil</ModalTitle>
            <ModalMessage>
              Tem certeza que deseja excluir este funil? Esta ação não pode ser desfeita e irá excluir:
              <br /><br />
              • O funil <strong>{formData.nome}</strong>
              <br />
              • Todas as {etapas.length} etapa(s) associadas
              <br />
              • Todas as mensagens das etapas
              <br />
              • Todos os controles de prospecção relacionados
              <br /><br />
              <strong>Atenção:</strong> Se existirem leads associados a este funil, a exclusão não será permitida.
            </ModalMessage>
            <ModalActions>
              <ModalButton onClick={handleCancelarExclusaoFunil}>
                Cancelar
              </ModalButton>
              <ModalButton $danger onClick={handleConfirmarExclusaoFunil} disabled={deletandoFunil}>
                <FiTrash2 />
                {deletandoFunil ? 'Excluindo...' : 'Excluir Funil'}
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </Layout>
  );
};

export default FunilProspeccaoForm;

